'use client'

import { useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import AppBreadcrumb from '@/components/partials/layout/app-breadcrumb'
import { useProductsActiveCategory } from './products-category-nav'
import { useAppLocale, useTranslatedRoute } from '@/hooks'
import { getLocalizedValue } from '@/lib/utils'
import { CategoryType } from '@/types/category'

type PropsType = {
  categories: CategoryType[]
}

/**
 * Breadcrumb for the products' layout. Derives the active category from the pathname on the client, so
 * it stays mounted across navigations and updates instantly. The SEO breadcrumb (JSON-LD) is rendered
 * separately, server-side, in the products page.
 */
const ProductsBreadcrumb = ({ categories }: PropsType) => {
  const t = useTranslations()
  const searchParams = useSearchParams()
  const { backendLocale } = useAppLocale()
  const { buildRoute } = useTranslatedRoute()

  const activeCategory = useProductsActiveCategory(categories)

  const items = useMemo(() => {
    const productsRoute = buildRoute('products')
    const result: { label: string; href?: string }[] = [{ label: t('products'), href: productsRoute }]

    if (activeCategory) {
      const slugPath: string[] = []
      const rootToImmediate = [...(activeCategory.parents ?? [])].reverse()

      for (const parent of rootToImmediate) {
        const parentName = getLocalizedValue(parent, 'name', backendLocale)
        const parentSlug = getLocalizedValue(parent, 'slug', backendLocale)
        if (parentName && parentSlug) {
          slugPath.push(parentSlug)
          result.push({ label: parentName, href: `${productsRoute}/${slugPath.join('/')}` })
        }
      }

      const categoryName = getLocalizedValue(activeCategory, 'name', backendLocale)
      const categorySlug = getLocalizedValue(activeCategory, 'slug', backendLocale)
      if (categorySlug) {
        slugPath.push(categorySlug)
      }
      result.push({ label: categoryName, href: `${productsRoute}/${slugPath.join('/')}` })
    }

    const hasFilterParams =
      !!searchParams.get('q') ||
      !!searchParams.get('applications') ||
      !!searchParams.get('brands') ||
      !!searchParams.get('models')
    if (hasFilterParams) {
      result.push({ label: t('search_results'), href: '' })
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, backendLocale, searchParams])

  return <AppBreadcrumb items={items} fullWidth />
}

export default ProductsBreadcrumb
